import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  DatePicker,
  Button,
  Typography,
  Select,
} from '@surecompanies/core_components'
import { ReloadOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import dayjs from 'dayjs'

const { Text } = Typography

const MAX_RANGE_MONTHS = 2

type QuickRange =
  | { label: string; days: number }
  | { label: string; months: number }
  | { label: string; exactDaysAgo: number }
  | { label: string; realtime: true }

const REALTIME_LABEL = 'Realtime'

const QUICK_RANGES: QuickRange[] = [
  { label: REALTIME_LABEL, realtime: true },
  { label: 'Today', days: 0 },
  { label: 'Yesterday', exactDaysAgo: 1 },
  { label: 'Before yesterday', exactDaysAgo: 2 },
  { label: '3 Days', days: 2 },
  { label: '7 Days', days: 6 },
  { label: '15 Days', days: 14 },
  { label: '1 Month', months: 1 },
  { label: '2 Months', months: 2 },
]

/** Compute the fromDate for a given quick range relative to `current`. */
export function resolveQuickRange(
  range: Exclude<QuickRange, { realtime: true }>,
  current: dayjs.Dayjs
): dayjs.Dayjs {
  if ('exactDaysAgo' in range) {
    return current.subtract(range.exactDaysAgo, 'day').startOf('day')
  }
  if ('days' in range) {
    return range.days === 0
      ? current.startOf('day')
      : current.subtract(range.days, 'day')
  }
  return current.subtract(range.months, 'month')
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Divider = styled.span`
  width: 1px;
  height: 24px;
  background: #d9d9d9;
  margin: 0 4px;
`

export interface DateRangeValue {
  fromDate?: dayjs.Dayjs
  toDate?: dayjs.Dayjs
}

interface DateRangeFilterProps {
  value: DateRangeValue
  onChange: (value: DateRangeValue) => void
  loading?: boolean
  onRefresh?: () => void
  timezone?: string
  defaultQuickRange?: string
  /** Called whenever the user selects a quick-range option (or clears it). */
  onQuickRangeChange?: (range: string | undefined) => void
}

export function isDateRangeValid(
  from?: dayjs.Dayjs,
  to?: dayjs.Dayjs
): boolean {
  if (!from || !to) return true
  return !from.isAfter(to) && to.diff(from, 'month', true) <= MAX_RANGE_MONTHS
}

const quickRangeOptions = QUICK_RANGES.map((r) => ({
  label: r.label,
  value: r.label,
}))

export default function DateRangeFilter({
  value,
  onChange,
  loading,
  onRefresh,
  timezone,
  defaultQuickRange = 'Today',
  onQuickRangeChange,
}: DateRangeFilterProps) {
  // Initialise the quick-range label from the prop.
  // Special-case "Realtime": if the component mounts with dates already set
  // (e.g. restored from a URL but no saved range label), "Realtime" would be
  // misleading, so fall back to the placeholder.  Any other named range (e.g.
  // "3 Days") is trusted as-is — it was restored from the URL alongside the
  // matching dates.
  const [quickRange, setQuickRange] = useState<string | undefined>(() => {
    if ((value.fromDate || value.toDate) && defaultQuickRange === 'Realtime') {
      return undefined
    }
    return defaultQuickRange
  })

  const isRealtime = !value.fromDate && !value.toDate
  const fromAfterTo =
    !isRealtime &&
    !!value.fromDate &&
    !!value.toDate &&
    value.fromDate.isAfter(value.toDate)
  const exceeded =
    !isRealtime &&
    !fromAfterTo &&
    !!value.fromDate &&
    !!value.toDate &&
    value.toDate.diff(value.fromDate, 'month', true) > MAX_RANGE_MONTHS
  const hasError = fromAfterTo || exceeded

  const now = useCallback(
    () => (timezone ? dayjs().tz(timezone) : dayjs()),
    [timezone]
  )

  // When a relative quick-range label is restored from the URL the stored
  // from/to dates may be stale (e.g. a "Yesterday" link opened a day later).
  // Recompute fresh dates on mount so the query always uses today's values.
  const didRecomputeRef = useRef(false)
  useEffect(() => {
    if (didRecomputeRef.current) return
    didRecomputeRef.current = true
    if (!defaultQuickRange || defaultQuickRange === REALTIME_LABEL) return
    const range = QUICK_RANGES.find((r) => r.label === defaultQuickRange)
    if (!range || 'realtime' in range) return
    const current = now()
    const fromDate = resolveQuickRange(range, current)
    const toDate = 'exactDaysAgo' in range ? fromDate.endOf('day') : current
    onChange({ fromDate, toDate })
  }, [defaultQuickRange, now, onChange])

  const handleFromChange = useCallback(
    (d: dayjs.Dayjs | null) => {
      if (d) {
        setQuickRange(undefined)
        onQuickRangeChange?.(undefined)
        onChange({ ...value, fromDate: d })
      }
    },
    [value, onChange, onQuickRangeChange]
  )

  const handleToChange = useCallback(
    (d: dayjs.Dayjs | null) => {
      if (d) {
        setQuickRange(undefined)
        onQuickRangeChange?.(undefined)
        onChange({ ...value, toDate: d })
      }
    },
    [value, onChange, onQuickRangeChange]
  )

  const handleQuickRange = useCallback(
    (val: string) => {
      setQuickRange(val)
      onQuickRangeChange?.(val)
      if (val === REALTIME_LABEL) {
        onChange({ fromDate: undefined, toDate: undefined })
        return
      }
      const range = QUICK_RANGES.find((r) => r.label === val)
      if (!range || 'realtime' in range) return
      const current = now()
      const fromDate = resolveQuickRange(range, current)
      const toDate = 'exactDaysAgo' in range ? fromDate.endOf('day') : current
      onChange({ fromDate, toDate })
    },
    [onChange, onQuickRangeChange, now]
  )

  return (
    <Wrapper>
      <Row>
        <Divider />

        <Select
          options={quickRangeOptions}
          onChange={handleQuickRange}
          placeholder="Quick range"
          value={quickRange ?? null}
          style={{ width: 140 }}
          allowClear={false}
        />

        {!isRealtime && (
          <>
            <Divider />

            <Text>From:</Text>
            <DatePicker
              value={value.fromDate}
              onChange={handleFromChange}
              format="MM/DD/YYYY"
              allowClear={false}
              status={hasError ? 'error' : undefined}
              style={{ width: 140 }}
            />
            <Text>To:</Text>
            <DatePicker
              value={value.toDate}
              onChange={handleToChange}
              format="MM/DD/YYYY"
              allowClear={false}
              status={hasError ? 'error' : undefined}
              style={{ width: 140 }}
            />
          </>
        )}

        {onRefresh && (
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={onRefresh}
            disabled={hasError}
          >
            Refresh
          </Button>
        )}
      </Row>
    </Wrapper>
  )
}
